import { TenantRole } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useTenants } from '@/services/tenants/useTenants';
import { ArrowDropDown } from '@mui/icons-material';
import { Button, List, ListItem, ListItemText, Popover } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const StyledPopover = styled(Popover)(() => ({
  '& span': {
    fontSize: '15px',
  },
}));

const StyledList = styled(List)(() => ({
  padding: 0,
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: 0,
  '&:first-of-type': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const StyledLink = styled(Link)(({ theme }) => ({
  display: 'block',
  width: '100%',
  padding: '12px 16px',
  textDecoration: 'none',
  color: 'inherit',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const TenantSelector: React.FC = () => {
  const { getTenantId } = useTenants();
  const { data: user } = useGetCurrentUser();

  const currentTenantId = getTenantId();
  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | undefined>();

  const settings = user?.user?.settings?.filter(
    tenant => tenant.tenantRole !== TenantRole.Search && tenant.userEnabled
  );
  const currentTenant = settings?.find(tenant => tenant.tenantId === currentTenantId) || user?.user?.settings?.[0];

  const handleTenantClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(undefined);
  };

  const open = Boolean(anchorElement);

  if (!settings?.length) {
    return;
  } else if (settings.length === 1) {
    return <BodyText sx={{ color: '#ffffff' }}>{currentTenant?.tenantDisplayName || 'Tenant'}</BodyText>;
  } else {
    return (
      <>
        <Button
          variant='contained'
          sx={{
            textTransform: 'none',
            flexShrink: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
          }}
          onClick={handleTenantClick}
          endIcon={<ArrowDropDown sx={{ color: '#ffffff' }} />}
        >
          <BodyText sx={{ color: '#ffffff' }}>{currentTenant?.tenantDisplayName || 'Select Tenant'}</BodyText>
        </Button>
        <StyledPopover
          open={open}
          anchorEl={anchorElement}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              sx: {
                marginTop: '4px',
                minWidth: '200px',
              },
            },
          }}
        >
          <StyledList>
            {settings.map(tenant => (
              <StyledListItem
                key={tenant.tenantId}
                sx={{
                  borderBottom: tenant.tenantId === settings.at(-1)?.tenantId ? 'none' : undefined,
                }}
              >
                <StyledLink to={`/tenants/${tenant.tenantShortName}/assets`} onClick={handleClose}>
                  <ListItemText primary={tenant.tenantDisplayName} />
                </StyledLink>
              </StyledListItem>
            ))}
          </StyledList>
        </StyledPopover>
      </>
    );
  }
};
